<template>
  <div>
    <div class="tla-form-group tla-form-group--margin-medium">
      <label class="tla-form-group__label tla-form-group__label--required" v-html="label"></label>
      <NumberInput
        :id="name"
        :name="name"
        v-model="currentValue"
        :min="min"
        :max="max"
        :step="step"
        :precision="precision"
        :prefix="prefix"
        :suffix="suffix"
        :slider="slider"
        :fieldName="'numberInputTouchPoint'"
        v-validate.initial="`touchPointNumberInput:${currentTouchPointState}`"
        :data-vv-name="name"
        @numberInputTouchPoint="updateValue($event)"
        :moneyValidationClass="getValidationClass(name, 'input', currentValueChanged)"
        :data-vv-scope="validationScope">
      </NumberInput>
      <span class="tla-form-group__message" :class="getValidationClass(name, 'message', currentValueChanged)">{{ errors.first(`${validationScope}.${name}`) }}</span>
      <vue-slider v-if="!hideSliders" v-model="currentValue" :min="min" :max="max" height="10" :dot-size="26" :tooltip="false" :interval="interval" :class="{ 'vue-slider-component--no-value': currentValue === min }"></vue-slider>
      <div class="tla-form-group__touch-points tla-form-group__touch-points--spaced">
        <touchpoint
          :collectionName="name"
          :inputId="inputId"
          :inputValue="inputValue"
          :validationRules="validationRules"
          :isFullWidth="true"
          :isSelected="currentTouchPointState"
          :validationScope="validationScope"
          ref="touchpointComponent">
          <div class="generic-touchpoint__text" slot="touchpoint">{{inputValue}}</div>
        </touchpoint>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch, Ref, Inject
} from 'vue-property-decorator';
import vueSlider from 'vue-slider-component';
import VeeValidate, { Validator } from 'vee-validate';
import { validationService } from '@/services/ValidationService';
import mobileCheckService from '@/services/MobileCheckService';
import NumberInput from '../widgets/common/NumberInput.vue';
import GenericTouchPointTextComponent from './GenericTouchPointTextComponent.vue';
import GenericTouchPointComponent from './GenericTouchPointComponent.vue';

Validator.extend('touchPointNumberInput', {
  getMessage: (field: string) => 'Please enter a valid amount.',
  validate: (input: number, params: any) => (params[0] === 'true' || input > 0)
});

Vue.use(VeeValidate);

@Component({
  components: {
  touchpoint: GenericTouchPointComponent,
  NumberInput,
  vueSlider,
  touchpointText: GenericTouchPointTextComponent
  }
  })
export default class NumberInputTouchPointComponent extends Vue {
  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 1 })
  public step!: number;

  @Prop({ default: Infinity })
  public max!: number;

  @Prop({ default: -Infinity })
  public min!: number;

  @Prop({ default: true })
  public controls!: boolean;

  @Prop({ default: false })
  public slider!: boolean;

  @Prop(String)
  public name!: string;

  @Prop(String)
  public placeholder!: string;

  @Prop({ default: 2 })
  public precision!: number;

  @Prop({ default: '£' })
  public prefix!: string;

  @Prop({ default: '' })
  public suffix!: string;

  @Prop({ default: '' })
  public fieldName!: string;

  @Prop({ default: null })
  public inputId!: string;

  @Prop({ default: null })
  public inputValue: any;

  @Prop({ default: '' })
  public validationRules!: string;

  @Prop()
  public value!: number;

  @Prop({ default: '' })
  public validationScope!: string;

  @Prop({ default: '' })
  public label!: string;

  @Prop({ default: 1 })
  public interval!: number;

  @Ref()
  private readonly touchpointComponent!: GenericTouchPointComponent;

  public currentTouchPointState: boolean = false;

  private currentValueChanged: boolean = false;

  private currentValue: number = 0;
  
  private hideSliders: boolean = false;

  @Watch('currentValue')
  public onCurrentValueChange(newVal: number, oldVal: number) {
    if (this.currentValue > 0 && this.currentTouchPointState) {
      this.currentTouchPointState = false;
    }

    this.$emit(this.name, this.currentValue, this.currentTouchPointState);
    this.currentValueChanged = oldVal !== newVal;
  }

  @Watch('value', { immediate: true })
  public onValueChanged(newValue: number, oldValue: number) {
    if (newValue && newValue !== this.currentValue
    ) {
      this.currentValue = newValue;
    }
  }

  public updateTouchpointState() {
    const isZero = this.currentValue === 0;

    this.currentValue = 0;
    this.currentTouchPointState = true;

    if (isZero) {
      this.$emit(this.name, this.currentValue, this.currentTouchPointState);
    }
  }

  public updateValue(newValue: number) {
    this.currentValue = newValue;
  }

  public getValidationClass(fieldName: string, fieldType?: string, fieldChanged?: boolean) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, fieldChanged, false, this.validationScope);
  }

  public mounted() {
    this.touchpointComponent.$on(this.name, this.updateTouchpointState);
    this.hideSliders = mobileCheckService.hideSliders;
  }
}
</script>
